import * as R from 'ramda';
import { put, call, take, fork, cancel, delay, select, takeLatest } from 'redux-saga/effects';
// components
import { closeModal } from '../../components/modal/actions';
import { closeLoader, openLoader } from '../../components/loader/actions';
// common
import { setSocketConnection } from '../../common/actions';
import { makeSelectSocketConnected } from '../../common/selectors';
// features
import { messageType } from '../sockets/constants';
import { switchBranchRequest } from '../branch/actions';
import { socketPostMessage } from '../sockets/actions';
import { makeSelectCurrentBranchGuid } from '../branch/selectors';
import { getAllAvailableRefTypesByScopeRequest } from '../reference/actions';
// helpers/constants
import * as G from '../../helpers';
import * as GC from '../../constants';
import { ENUMS } from '../../constants/enums';
// sagas
import { crudSaga, visitPageSaga } from '../../sagas';
// utilities
import { sendRequest } from '../../utilities/http';
import endpointsMap from '../../utilities/endpoints';
// feature drivers-card
import * as A from './actions';
import {
  GROUP_TYPE_NONE,
  FILTER_TYPE_NONE,
  GROUP_TYPE_DISPATCHER,
  GROUP_TYPE_DISPATCHING_GROUP,
  GROUP_TYPE_GROUP_AND_DISPATCHER,
} from './constants';
import {
  makeSelectFilters,
  makeSelectGroupBy,
  makeSelectDispatchByTruck,
  makeSelectTelNotifications,
  makeSelectGlobalFilterValue,
  makeSelectLoadRefFilterValue,
  makeSelectSearchLoadsByCustomerFilter,
} from './selectors';
import {
  getCardHeight,
  getRequestFilters,
  getDefaultFilterRequestDates,
  getGuidsFromGroupedAssignedLoads,
  getTelGuidsFromDriverCardListItems,
} from './helpers';
//////////////////////////////////////////////////

function* getUnassignedLoadsRequest({ payload }) {
  try {
    // TODO: with pagination
    const showLoader = R.path(['data', 'showLoader'], payload);

    if (G.isTrue(showLoader)) yield put(openLoader());

    const options = {
      data: R.mergeRight(payload.data, {
        offset: 0,
        limit: 200,
        radiusUom: G.getMileOrKmUomByUomSystem(),
      }),
    };

    yield call(crudSaga, {
      options,
      method: 'post',
      endpoint: endpointsMap.unassignedLoads,
      parentSagaName: 'getUnassignedLoadsRequest',
      successAction: A.getUnassignedLoadsSuccess,
    });

    if (G.isTrue(showLoader)) yield put(closeLoader());
  } catch (error) {
    yield call(G.handleException, error, 'getUnassignedLoadsRequest exception');
  }
}

function* getDriverCardsInfoRequest({ payload }) {
  try {
    yield call(crudSaga, {
      method: 'get',
      endpoint: endpointsMap.telDriverCardsInfo,
      parentSagaName: 'getDriverCardsInfoRequest',
      successAction: A.getDriverCardsInfoSuccess,
      options: { params: { enterpriseGuid: payload } },
    });
  } catch (error) {
    yield call(G.handleException, error, 'getDriverCardsInfoRequest exception');
  }
}

function* getDriverCardsByTruckInfoRequest({ payload }) {
  try {
    yield call(crudSaga, {
      method: 'get',
      endpoint: endpointsMap.telDriverCardsByTruckInfo,
      options: { params: { enterpriseGuid: payload } },
      parentSagaName: 'getDriverCardsByTruckInfoRequest',
      successAction: A.getDriverCardsByTruckInfoSuccess,
    });
  } catch (error) {
    yield call(G.handleException, error, 'getDriverCardsInfoRequest exception');
  }
}

function* handleGetTelNotificationsByTelGuidsSaga({ payload }) {
  try {
    const options = {
      data: { elements: payload },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.notificationList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getTelNotificationsSuccess(data.results));
    } else {
      yield call(G.handleFailResponse, res, 'handleGetTelNotificationsByTelGuidsSaga');
    }
  } catch (error) {
    yield call(G.handleException, error, 'handleGetTelNotificationsByTelGuidsSaga');
  }
}

function* handleHideTelNotificationsSaga({ payload }) {
  try {
    yield put(openLoader());

    const notifications = yield select(makeSelectTelNotifications());

    const data = {
      elements: R.compose(
        R.map(({ userNotificationGuid }) => userNotificationGuid),
        R.values,
        R.path([payload]),
      )(notifications),
    };

    const res = yield call(sendRequest, 'put', endpointsMap.hideWarnings, { data });

    if (G.isResponseSuccess(res.status)) {
      yield put(A.hideTelNotificationsSuccess(payload));
    } else {
      yield call(G.handleFailResponse, res, 'handleHideTelNotificationsSaga');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'handleHideTelNotificationsSaga');
  }
}

function* handleMarkAsReadTelNotificationRequestSaga({ payload }) {
  try {
    yield put(openLoader());

    const { guid, objectGuid, userNotificationGuid } = payload;

    const options = {
      data: {
        read: true,
        guids: [userNotificationGuid],
      },
    };

    const res = yield call(sendRequest, 'put', endpointsMap.notificationMark, options);

    const { status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.markAsReadTelNotificationSuccess({ guid, objectGuid }));
    }
  } catch (err) {
    yield call(G.handleException, err, 'handleMarkAsReadTelNotificationRequestSaga exception');
  } finally {
    yield put(closeLoader());
  }
}

function* getGroupedAssignedLoadsSaga({ payload }) {
  try {
    const { groupBy, globalFilterValue } = payload.data;

    yield put(A.setValueToStore({ value: [], path: 'openedGroups' }));

    if (R.equals(groupBy, GROUP_TYPE_DISPATCHING_GROUP)) {
      yield put(A.setValueToStore({ value: {}, path: 'loadsByDispatchGroup' }));

      if (G.isNilOrEmpty(globalFilterValue)) return;

      return yield put(A.getDriverLoadsByDispatchGroupRequest(payload.data));
    }

    if (R.equals(groupBy, GROUP_TYPE_GROUP_AND_DISPATCHER)) {
      yield put(A.setValueToStore({ value: {}, path: 'loadsByDispatchGroupAndDispatchers' }));

      return yield put(A.getLoadsByDispatchGroupAndDispatcherRequest(payload));
    }

    if (R.equals(groupBy, GROUP_TYPE_DISPATCHER)) {
      yield put(A.setValueToStore({ value: {}, path: 'driverLoadsByDispatcher' }));

      if (G.isNilOrEmpty(globalFilterValue)) return;

      return yield put(A.getDriverLoadsByDispatcherRequest(payload.data));
    }
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'getGroupedAssignedLoadsSaga');
  }
}

function* getDriverLoadsByDispatchGroupRequest({ payload }) {
  try {
    yield put(openLoader());

    const { dispatchingGroupGuid } = payload;

    const groupBy = yield select(makeSelectGroupBy());
    const filtersStore = yield select(makeSelectFilters());
    const dispatchByTruck = yield select(makeSelectDispatchByTruck());
    const referenceValue = yield select(makeSelectLoadRefFilterValue());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());
    const enterpriseGuid = yield select(makeSelectSearchLoadsByCustomerFilter());

    const dispatchByTruckFromPayload = R.prop('dispatchByTruck', payload);

    const currentEnterprise = G.getAmousCurrentBranchGuidFromWindow();

    const { dateTo, dateFrom } = filtersStore;

    // TODO: with pagination
    const options = {
      data: {
        ...filtersStore,
        groupBy,
        offset: 0,
        limit: 200,
        enterpriseGuid,
        referenceValue,
        showTeam: true,
        globalFilterValue,
        currentEnterprise,
        dispatchingGroupGuid,
        dateTo: G.toEndOfDayFromDate(dateTo),
        dateFrom: G.toStartOfDayFromDate(dateFrom),
      },
    };

    const endpoint = G.isTrue(dispatchByTruck) || G.isTrue(dispatchByTruckFromPayload) ?
      endpointsMap.telDriverCardsByTruckAssignedLoadsList : endpointsMap.assignedLoadsList;

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getLoadsByDispatchGroupSuccess({ data, dispatchingGroupGuid }));

      const telGuids = getTelGuidsFromDriverCardListItems(data);

      if (G.isNotEmpty(telGuids)) {
        yield fork(handleGetTelNotificationsByTelGuidsSaga, { payload: telGuids });
      }
    } else {
      yield call(G.handleFailResponse, res, 'getDriverLoadsByDispatchGroupRequest');
    }

    G.scrollIntoElementView({ elementId: 'driver-card-scroll-to-id' });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'getDriverLoadsByDispatchGroupRequest');
  }
}

function* getLoadsByDispatchGroupAndDispatcherRequest({ payload }) {
  try {
    yield put(openLoader());

    const { dispatcherGuid, dispatchingGroupGuid } = payload;

    const filtersStore = yield select(makeSelectFilters());
    const dispatchByTruck = yield select(makeSelectDispatchByTruck());
    const referenceValue = yield select(makeSelectLoadRefFilterValue());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());
    const enterpriseGuid = yield select(makeSelectSearchLoadsByCustomerFilter());

    const currentEnterprise = G.getAmousCurrentBranchGuidFromWindow();

    const { dateTo, dateFrom } = filtersStore;

    if (G.isAllTrue(
      G.isNilOrEmpty(dispatcherGuid),
      G.isNilOrEmpty(dispatchingGroupGuid),
      G.isNilOrEmpty(globalFilterValue),
    )) return yield put(closeLoader());

    // TODO: with pagination
    const options = {
      data: {
        ...filtersStore,
        offset: 0,
        limit: 200,
        referenceValue,
        showTeam: true,
        dispatcherGuid,
        enterpriseGuid,
        globalFilterValue,
        currentEnterprise,
        dispatchingGroupGuid,
        dateTo: G.toEndOfDayFromDate(dateTo),
        groupBy: GROUP_TYPE_DISPATCHING_GROUP,
        dateFrom: G.toStartOfDayFromDate(dateFrom),
      },
    };

    const endpoint = G.isTrue(dispatchByTruck) ?
      endpointsMap.telDriverCardsByTruckAssignedLoadsList : endpointsMap.assignedLoadsList;

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getLoadsByDispatchGroupAndDispatcherSuccess({ data, dispatcherGuid, dispatchingGroupGuid }));

      const telGuids = getTelGuidsFromDriverCardListItems(data);

      if (G.isNotEmpty(telGuids)) {
        yield fork(handleGetTelNotificationsByTelGuidsSaga, { payload: telGuids });
      }
    } else {
      yield call(G.handleFailResponse, res, 'getLoadsByDispatchGroupAndDispatcherRequest');
    }

    G.scrollIntoElementView({ elementId: 'driver-card-scroll-to-id' });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'getLoadsByDispatchGroupAndDispatcherRequest');
  }
}

function* getDriverLoadsByDispatcherRequest({ payload }) {
  try {
    yield put(openLoader());

    const { dispatcherGuid } = payload;

    const groupBy = yield select(makeSelectGroupBy());
    const filtersStore = yield select(makeSelectFilters());
    const referenceValue = yield select(makeSelectLoadRefFilterValue());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());
    const enterpriseGuid = yield select(makeSelectSearchLoadsByCustomerFilter());

    const currentEnterprise = G.getAmousCurrentBranchGuidFromWindow();

    const { dateTo, dateFrom } = filtersStore;

    // TODO: with pagination
    const options = {
      data: {
        ...filtersStore,
        groupBy,
        offset: 0,
        limit: 200,
        showTeam: true,
        dispatcherGuid,
        enterpriseGuid,
        referenceValue,
        globalFilterValue,
        currentEnterprise,
        dateTo: G.toEndOfDayFromDate(dateTo),
        dateFrom: G.toStartOfDayFromDate(dateFrom),
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.assignedLoadsList, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      yield put(A.getDriverLoadsByDispatcherSuccess({ data, dispatcherGuid }));

      const telGuids = getGuidsFromGroupedAssignedLoads(data);

      if (G.isNotEmpty(telGuids)) {
        yield fork(handleGetTelNotificationsByTelGuidsSaga, { payload: telGuids });
      }
    } else {
      yield call(G.handleFailResponse, res, 'getDriverLoadsByDispatcherRequest');
    }

    G.scrollIntoElementView({ elementId: 'driver-card-scroll-to-id' });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'getDriverLoadsByDispatcherRequest');
  }
}

function* getNotGroupedAssignedLoadsSaga({ payload }) {
  try {
    yield put(openLoader());

    const dispatchByTruck = yield select(makeSelectDispatchByTruck());
    const referenceValue = yield select(makeSelectLoadRefFilterValue());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());
    const enterpriseGuid = yield select(makeSelectSearchLoadsByCustomerFilter());

    const dispatchByTruckFromPayload = R.prop('dispatchByTruck', payload);

    const globalFilterValueFromPayload = R.path(['data', 'globalFilterValue'], payload);

    // TODO: with pagination
    const options = {
      data: {
        ...payload.data,
        offset: 0,
        limit: 200,
        referenceValue,
        showTeam: true,
        enterpriseGuid,
        globalFilterValue: G.ifElse(
          G.isNotNilAndNotEmpty(globalFilterValueFromPayload),
          globalFilterValueFromPayload,
          globalFilterValue,
        ),
      },
    };

    const endpoint = G.isTrue(dispatchByTruck) || G.isTrue(dispatchByTruckFromPayload) ?
      endpointsMap.telDriverCardsByTruckAssignedLoadsList : endpointsMap.assignedLoads;

    const res = yield call(sendRequest, 'post', endpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const dataToUse = G.isTrue(dispatchByTruck) || G.isTrue(dispatchByTruckFromPayload) ?
        data : R.pathOr([], [0, 'driverLoads'], data);

      yield put(A.getNotGroupedAssignedLoadsSuccess(dataToUse));

      const telGuids = getTelGuidsFromDriverCardListItems(dataToUse);

      if (G.isNotEmpty(telGuids)) {
        yield fork(handleGetTelNotificationsByTelGuidsSaga, { payload: telGuids });
      }
    } else {
      yield call(G.handleFailResponse, res, 'getNotGroupedAssignedLoadsSaga');
    }

    G.scrollIntoElementView({ elementId: 'driver-card-scroll-to-id' });

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'getNotGroupedAssignedLoadsSaga');
  }
}

function* handleCreateDriverOnDriversCardSaga({ payload }) {
  try {
    yield put(openLoader());

    const fleetAssignment = R.pick(GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR, payload);

    const omittedPayload = R.omit([
      GC.FIELD_FLEET_ASSIGNMENT,
      ...GC.GROUPED_FIELDS.FLEET_ASSIGNMENT_PICK_ARR,
    ], payload);

    const options = {
      data: {
        ...omittedPayload,
        fleetAssignment,
      },
    };

    const res = yield call(sendRequest, 'post', endpointsMap.telFleetRate, options);

    if (G.isResponseSuccess(res.status)) {
      yield put(closeModal());
      yield call(G.showToastrMessage, 'success', 'messages:success:create');

      const groupBy = yield select(makeSelectGroupBy());
      const filtersStore = yield select(makeSelectFilters());
      const referenceValue = yield select(makeSelectLoadRefFilterValue());
      const globalFilterValue = yield select(makeSelectGlobalFilterValue());

      const { action, options } = getRequestFilters(
        groupBy,
        filtersStore,
        referenceValue,
        globalFilterValue,
        A.getGroupedAssignedLoadsRequest,
        A.getNotGroupedAssignedLoadsRequest,
      );

      yield put(A.createDriverOnDriversCardSuccess(payload));
      yield put(action(options));
    } else {
      yield call(G.handleFailResponse, res, 'handleCreateDriverOnDriversCardSaga');
    }

    yield put(closeLoader());
    yield put(closeModal());
  } catch (error) {
    yield put(closeModal());
    yield put(closeLoader());

    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
    yield call(G.handleException, error, 'handleCreateDriverOnDriversCardSaga');
  }
}

function* refreshAssignedLoadsSaga() {
  try {
    yield put(openLoader());

    const groupBy = yield select(makeSelectGroupBy());
    const filtersStore = yield select(makeSelectFilters());
    const referenceValue = yield select(makeSelectLoadRefFilterValue());
    const globalFilterValue = yield select(makeSelectGlobalFilterValue());

    const { action, options } = getRequestFilters(
      groupBy,
      filtersStore,
      referenceValue,
      globalFilterValue,
      A.getGroupedAssignedLoadsRequest,
      A.getNotGroupedAssignedLoadsRequest,
    );

    yield put(action(options));
    yield put(closeLoader());
    yield put(closeModal());
  } catch (error) {
    yield put(closeLoader());

    yield call(G.handleException, error, 'refreshAssignedLoadsSaga');
    yield call(G.showToastrMessage, 'error', 'messages:error:unknown');
  }
}

const CONFIGS_ARR = [
  GC.UI_DRIVER_CARDS_ZOOM,
  GC.UI_DRIVER_CARDS_CARD_VIEW,
  GC.UI_DRIVER_CARDS_CARD_HEIGHT,
  GC.UI_DRIVER_CARDS_WARNING_PERIOD,
  GC.UI_DRIVER_CARDS_DEFAULT_GROUP_BY,
  GC.UI_DRIVER_CARDS_DEFAULT_DAY_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_GROUP_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_DRIVER_FILTER,
  GC.UI_DRIVER_CARDS_DEFAULT_SEARCH_RADIUS,
  GC.UI_DRIVER_CARDS_DEFAULT_MIN_EMPTY_HOURS,
  GC.UI_DRIVER_CARDS_SHOW_LOCATION_NAME_ON_HYBRID_CARD,
];

function* getBranchConfigsAndData() {
  try {
    yield put(openLoader({ showDimmer: true }));

    const enterpriseGuid = yield select(makeSelectCurrentBranchGuid());

    const options = {
      params: {
        names: R.join(',', CONFIGS_ARR),
        [GC.FIELD_BRANCH_GUID]: enterpriseGuid,
      },
    };

    const res = yield call(sendRequest, 'get', endpointsMap.branchConfigsEndpoint, options);

    const { data, status } = res;

    if (G.isResponseSuccess(status)) {
      const mappedConfigs = G.mapConfigValuesByName(data);

      const defaultGroupByConfig = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_DEFAULT_GROUP_BY,
        mappedConfigs,
      );

      const dispatchByTruck = R.equals(defaultGroupByConfig, ENUMS.ENUM_TRUCK);

      const groupFilter = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_DEFAULT_GROUP_FILTER,
        mappedConfigs,
      );

      const driverFilter = R.or(
        G.getConfigValueFromStore(
          GC.UI_DRIVER_CARDS_DEFAULT_DRIVER_FILTER,
          mappedConfigs,
        ),
        FILTER_TYPE_NONE,
      );

      const zoom = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_ZOOM,
        mappedConfigs,
      );

      const searchRadiusValue = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_DEFAULT_SEARCH_RADIUS,
        mappedConfigs,
      );

      const minEmptyHoursValue = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_DEFAULT_MIN_EMPTY_HOURS,
        mappedConfigs,
      );

      const dayFilter = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_DEFAULT_DAY_FILTER,
        mappedConfigs,
      );

      const cardView = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_CARD_VIEW,
        mappedConfigs,
      );

      const cardHeight = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_CARD_HEIGHT,
        mappedConfigs,
      );

      const showLocationName = G.getConfigValueFromStore(
        GC.UI_DRIVER_CARDS_SHOW_LOCATION_NAME_ON_HYBRID_CARD,
        mappedConfigs,
      );

      const dayFilterToUse = R.or(dayFilter, 7);
      const filters = getDefaultFilterRequestDates(dayFilterToUse);

      const { dateTo, dateFrom } = filters;

      const groupBy = R.or(groupFilter, GROUP_TYPE_NONE);

      yield put(A.setValueToStore({ value: cardView, path: 'cardView' }));
      yield put(A.setValueToStore({ value: cardHeight, path: 'cardHeightType' }));
      yield put(A.setValueToStore({ value: dispatchByTruck, path: 'dispatchByTruck' }));
      yield put(A.setValueToStore({ value: showLocationName, path: 'showLocationName' }));
      yield put(A.setValueToStore({ value: searchRadiusValue, path: 'searchRadiusValue' }));
      yield put(A.setValueToStore({ value: getCardHeight(cardHeight), path: 'cardHeight' }));
      yield put(A.setValueToStore({ value: minEmptyHoursValue, path: 'minEmptyHoursValue' }));

      yield put(A.setZoomToStore(R.or(zoom, 1)));
      yield put(A.setGroupByToStore(groupBy));
      yield put(A.setDateRangeFilters(filters));
      yield put(A.setFilterToStore(driverFilter));
      yield put(A.setDayFilterToStore(dayFilterToUse));

      const options = {
        dispatchByTruck,
        data: {
          dateTo,
          groupBy,
          dateFrom,
          filter: driverFilter,
          currentEnterprise: enterpriseGuid,
        },
      };

      yield put(A.getDriverCardsInfoRequest(enterpriseGuid));

      yield put(A.getDriverCardsByTruckInfoRequest(enterpriseGuid));

      if (G.isAllTrue(
        R.equals(groupBy, GROUP_TYPE_DISPATCHING_GROUP),
        R.equals(driverFilter, FILTER_TYPE_NONE),
        G.isFalse(dispatchByTruck),
      )) {
        yield put(closeLoader());

        return;
      }

      const action = G.ifElse(
        R.equals(groupBy, GROUP_TYPE_NONE),
        A.getNotGroupedAssignedLoadsRequest,
        A.getGroupedAssignedLoadsRequest,
      );

      yield put(action(options));
    } else {
      yield call(G.handleFailResponse, res, 'getBranchConfigs fail');
    }

    yield put(closeLoader());
  } catch (error) {
    yield put(closeLoader());
    yield call(G.handleException, error, 'getBranchConfigs exception');
  }
}

// socket
function* subscribeToSocketSaga({ payload }) {
  try {
    if (G.isTrue(payload)) {
      yield put(socketPostMessage({
        action: messageType.subscribe,
        destination: '/driverCards/tel',
      }));
    }
  } catch (error) {
    yield call(G.handleException, error, 'subscribeToSocketSaga exception');
  }
}

function* cancelSubscribeSaga() {
  try {
    const socketSubscribe = yield takeLatest(setSocketConnection, subscribeToSocketSaga);

    yield take(switchBranchRequest);

    yield cancel(socketSubscribe);
  } catch (error) {
    yield call(G.handleException, error, 'cancelSubscribeSaga exception');
  }
}

function* handleVisitDriversCardSaga({ payload }) {
  while (true) { // eslint-disable-line
    yield call(visitPageSaga, payload, GC.CHECK_VISIT_DRIVERS_CARD_PAGE);

    const socketConnected = yield select(makeSelectSocketConnected());

    if (socketConnected) {
      yield put(socketPostMessage({
        action: messageType.subscribe,
        destination: '/driverCards/tel',
      }));
    }

    yield fork(cancelSubscribeSaga);

    yield call(getBranchConfigsAndData);

    yield put(getAllAvailableRefTypesByScopeRequest(GC.LOAD_TYPE_TEL));

    break;
  }
}

function* onZoomSwitched({ payload }) {
  yield put(openLoader());
  yield delay(0);
  yield put(A.setZoomToStore(payload));
  yield delay(0);

  G.scrollIntoElementView({ elementId: 'driver-card-scroll-to-id' });

  yield put(closeLoader());
}

function* onGroupBySwitched({ payload }) {
  yield put(openLoader());
  yield delay(0);
  yield put(A.setGroupByToStore(payload));
  yield put(closeLoader());
}

function* driversCardWatcherSaga() {
  yield takeLatest(GC.VISIT_DRIVERS_CARD_PAGE, handleVisitDriversCardSaga);
  //
  yield takeLatest(A.onZoomSwitched, onZoomSwitched);
  yield takeLatest(A.onGroupBySwitched, onGroupBySwitched);
  yield takeLatest(A.getUnassignedLoadsRequest, getUnassignedLoadsRequest);
  yield takeLatest(A.getDriverCardsInfoRequest, getDriverCardsInfoRequest);
  yield takeLatest(A.refreshAssignedLoadsRequest, refreshAssignedLoadsSaga);
  yield takeLatest(A.hideTelNotificationsRequest, handleHideTelNotificationsSaga);
  yield takeLatest(A.getGroupedAssignedLoadsRequest, getGroupedAssignedLoadsSaga);
  yield takeLatest(A.getNotGroupedAssignedLoadsRequest, getNotGroupedAssignedLoadsSaga);
  yield takeLatest(A.getDriverCardsByTruckInfoRequest, getDriverCardsByTruckInfoRequest);
  yield takeLatest(A.getTelNotificationsRequest, handleGetTelNotificationsByTelGuidsSaga);
  yield takeLatest(A.getDriverLoadsByDispatcherRequest, getDriverLoadsByDispatcherRequest);
  yield takeLatest(A.createDriverOnDriversCardRequest, handleCreateDriverOnDriversCardSaga);
  yield takeLatest(A.getDriverLoadsByDispatchGroupRequest, getDriverLoadsByDispatchGroupRequest);
  yield takeLatest(A.markAsReadTelNotificationRequest, handleMarkAsReadTelNotificationRequestSaga);
  yield takeLatest(A.getLoadsByDispatchGroupAndDispatcherRequest, getLoadsByDispatchGroupAndDispatcherRequest);
}

export default driversCardWatcherSaga;
